// =============================================================================
// ui/tabs/ImportHistory.tsx — review and undo a file import.
// -----------------------------------------------------------------------------
// ── 🚨 THE ONLY PLACE IN THIS CLIENT THAT DELETES TRADES ──
//
// Everything else writes or updates. That makes the confirmation the feature:
// the count, the filename, and the fact that ANNOTATIONS GO WITH IT have to be
// on screen before the button is live, because a trade imported in March and
// graded in June loses the grade too.
//
// ⚠ NO "RESTORE". Beta keeps the undone trades in a session variable and offers
// to put them back. That is a real safety net in a client whose state is a
// local object — it re-pushes into `state.trades` and saves. Here the rows are
// GONE from Postgres the moment the delete returns, and "restore" would mean
// re-inserting rows that have already lost their database identity: new
// `created_at`, new `updated_at`, and any concurrent edit from another tab
// silently reverted.
//
// So the honest design is a confirmation the user has to read, not an undo of
// the undo. The copy says what beta's toast says afterwards — before, when it
// can still change the outcome.
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useSupabase } from '../state/supabase';
import { useAccounts } from '../state/accounts';
import {
  loadImportBatches, undoImportBatch, type ImportBatchSummary,
} from '../../io/sync/importBatches';
import { undoOpenPositionsForBatch } from '../../io/sync/openPositions';
import { loadReconcileTrades } from '../../io/sync/reconcileRead';
import { batchVerdicts, type BatchVerdict } from '../../selectors/reconcile';
import { reportFailure } from '../../io/diagnostics/errorLog';

type Phase = 'loading' | 'ready' | 'failed' | 'undoing';

/**
 * What undoing this import would cost, on the row itself.
 *
 * ── 🚨 THREE STATES, AND THE MIDDLE ONE IS THE POINT ──
 *
 *   safe        every position also exists in another source — provably lossless
 *   costly      N positions exist nowhere else, and they are what would go
 *   unshared    nothing else recorded these days at all
 *
 * ⚠ IT NEVER SAYS "REMOVE THIS". The old client's duplicate finder recommended,
 * and scored a **0% true-positive rate** on Josh's book (D142). This states what
 * is true and leaves the button where it already was.
 *
 * ⚠ AND A DIRECTION CONFLICT IS SHOWN WITHOUT A VERDICT ATTACHED. On 2026-08-05
 * the file was the one that had mis-paired the fills — but the identical signal
 * with the sources swapped would mean the opposite, so the honest rendering
 * points at the disagreement and stops.
 */
function BatchVerdictLine({ v, pending }: { v: BatchVerdict | null; pending: boolean }) {
  if (pending) return <div className="muted ih-verdict">Checking for other copies…</div>;
  if (!v) return null;

  const conflicts = v.conflicts.length
    ? [...new Set(v.conflicts.map((c) => c.contract))].join(', ')
    : null;

  return (
    <div className={`ih-verdict ${v.fullyDuplicated ? 'ok' : 'muted'}`}>
      {v.fullyDuplicated ? (
        <>
          <b>Every trade in this import also exists elsewhere.</b>
          {' Removing it would lose nothing.'}
        </>
      ) : v.sharedDays === 0 ? (
        <>{'Nothing else recorded these days — this import is the only record of them.'}</>
      ) : (
        <>
          <b>{v.uniquePositions} position{v.uniquePositions === 1 ? '' : 's'}</b>
          {` here exist${v.uniquePositions === 1 ? 's' : ''} in no other source`}
          {v.uniqueTrades !== v.uniquePositions ? ` (${v.uniqueTrades} trades)` : ''}
          {'. Removing this import would lose them.'}
        </>
      )}
      {conflicts ? (
        <>
          {' '}
          {/* ⚠ Amber, not red. It is a disagreement to look at, not an error. */}
          <span className="ih-verdict-warn">
            ⚠ Another source disagrees about direction on {conflicts}.
          </span>
        </>
      ) : null}
    </div>
  );
}

/** The same cost, restated on the confirmation. */
function ConfirmVerdict({ v }: { v: BatchVerdict | null }) {
  if (!v) return null;
  if (v.fullyDuplicated) {
    return (
      <p className="green">
        <b>Checked: every trade in this import also exists in another source.</b>
        {' Removing it loses no trading history.'}
      </p>
    );
  }
  if (v.uniquePositions === 0) return null;
  return (
    <p className="red">
      <b>
        {v.uniqueTrades} trade{v.uniqueTrades === 1 ? '' : 's'} here
        {v.uniqueTrades === 1 ? ' exists' : ' exist'} in no other source
      </b>
      {' — nothing else in your book recorded '}
      {v.uniquePositions === 1 ? 'that position' : 'those positions'}
      {'. Removing this import is the only copy going.'}
    </p>
  );
}

export function ImportHistory({ nonce, onUndone }: { nonce?: number; onUndone?: () => void }) {
  const { sb, userId } = useSupabase();
  const { accounts } = useAccounts();

  const [phase, setPhase] = useState<Phase>('loading');
  const [batches, setBatches] = useState<ImportBatchSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  /** The batch awaiting confirmation. Nothing is deleted until this is acted on. */
  const [confirming, setConfirming] = useState<ImportBatchSummary | null>(null);
  /**
   * Per-batch reconciliation — see `selectors/reconcile.ts`.
   *
   * 🚨 SEPARATE FROM THE LIST, AND FAILING SOFT. The history must render even if
   * this read dies: knowing you can undo an import is the feature, and knowing
   * whether it is safe is an improvement on it. A reconciliation error that took
   * the whole screen down would trade the thing for the improvement.
   */
  const [verdicts, setVerdicts] = useState<Map<string, BatchVerdict> | null>(null);
  const [reconcileFailed, setReconcileFailed] = useState(false);

  const reload = useCallback(async () => {
    setPhase('loading');
    setError(null);
    try {
      setBatches(await loadImportBatches(sb));
      setPhase('ready');
    } catch (e) {
      setError(reportFailure('import history', e));
      setPhase('failed');
    }

    // ⚠ AFTER the list, and in its own try. See `verdicts`.
    setVerdicts(null);
    setReconcileFailed(false);
    try {
      setVerdicts(batchVerdicts(await loadReconcileTrades(sb)));
    } catch {
      setReconcileFailed(true);
    }
  }, [sb]);

  // ⚠ `nonce` re-reads after an import lands, so a batch appears here without a
  // page refresh. Without it the history is stale exactly when someone looks.
  useEffect(() => { void reload(); }, [reload, nonce]);

  const nameOf = useCallback((id: string) =>
    (accounts ?? []).find((a) => String(a.id) === id)?.name || 'Unknown', [accounts]);

  const confirmUndo = useCallback(async (b: ImportBatchSummary) => {
    setPhase('undoing');
    setMessage(null);
    const r = await undoImportBatch(sb, b.tradeIds);
    setConfirming(null);

    // 🚨 REVERSE THE OPEN BOOK TOO, AND IN BOTH DIRECTIONS (#12): positions this
    // batch OPENED go away, and positions it CONSUMED come back. The second is
    // why consumed rows are marked rather than deleted — without it, undoing an
    // import would silently destroy a position that predates it.
    //
    // ⚠ AFTER the trades, and never allowed to block the undo. The trades are
    // the thing the user asked to remove; a stale open position is visible and
    // correctable, whereas a trade left behind after a confirmed undo is not.
    if (userId) {
      const p = await undoOpenPositionsForBatch(sb, userId, b.id);
      if (p.error) setError(`Trades removed. Open positions were not reversed: ${p.error}`);
    }

    if (r.error) {
      setError(r.error);
      setPhase('failed');
      // ⚠ Still reloads and still notifies: a PARTIAL delete changed the book,
      // and leaving both the list and the trades table stale would hide it.
      void reload();
      onUndone?.();
      return;
    }

    setMessage(`Removed ${r.deleted} trade${r.deleted === 1 ? '' : 's'} from ${b.filename}.`);
    await reload();
    onUndone?.();
  }, [sb, reload, onUndone]);

  return (
    <div className="card import-history" data-testid="import-history">
      <h3>Import history</h3>

      {phase === 'loading' ? <p className="muted" role="status">Reading your import history…</p> : null}
      {phase === 'undoing' ? <p className="muted" role="status">Removing trades…</p> : null}

      {error ? (
        <p className="red" role="alert">{error}</p>
      ) : null}

      {message ? <p className="green" role="status">{message}</p> : null}

      {phase === 'ready' && batches.length === 0 ? (
        // ⚠ Says WHY it is empty. A book full of trades with an empty import
        // history reads as broken unless it explains that only imports run from
        // here are tracked.
        <p className="muted">
          No tracked imports yet. Imports you run from now on appear here to review
          or undo — trades added before this existed carry no batch stamp.
        </p>
      ) : null}

      {batches.map((b) => (
        <div className="ih-row" key={b.id}>
          <div className="ih-what">
            <div className="ih-file">{b.filename}</div>
            <div className="muted ih-meta">
              {b.importedAt
                ? new Date(b.importedAt).toLocaleString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric',
                  hour: 'numeric', minute: '2-digit',
                })
                : '—'}
              {' · '}
              <b>{b.tradeIds.length}</b> trade{b.tradeIds.length === 1 ? '' : 's'}
              {' · '}
              {b.accountIds.length === 0
                ? '—'
                : b.accountIds.length <= 2
                  ? b.accountIds.map(nameOf).join(', ')
                  : `${b.accountIds.length} accounts`}
              {b.format ? ` · ${b.format}` : ''}
            </div>
            <BatchVerdictLine v={verdicts?.get(b.id) ?? null} pending={!verdicts && !reconcileFailed} />
          </div>
          <button
            type="button"
            className="btn danger"
            disabled={phase === 'undoing'}
            onClick={() => setConfirming(b)}
          >
            Undo import
          </button>
        </div>
      ))}

      {/* ── the confirmation. Nothing is deleted before this is answered. ──── */}
      {confirming ? (
        <div className="ih-confirm" role="alertdialog" aria-label="Confirm undo import">
          <p>
            <b>Remove {confirming.tradeIds.length} trade
            {confirming.tradeIds.length === 1 ? '' : 's'} from {confirming.filename}?</b>
          </p>
          {/* ⚠ THE SENTENCE THAT MATTERS. Beta says this in a toast AFTER the
              fact; it belongs before, where it can still change the answer. */}
          <p className="muted">
            This deletes them from your book, including any notes, grades, patterns
            or tags you have added since importing. It cannot be undone from here —
            you would have to import the file again, and the annotations would not
            come back.
          </p>
          {/* 🚨 THE COST, REPEATED AT THE MOMENT OF THE DECISION. The line on the
              row above is easy to have read a minute ago and forgotten; this is
              the last screen before the rows are gone. */}
          <ConfirmVerdict v={verdicts?.get(confirming.id) ?? null} />
          <div className="import-row">
            <button
              type="button"
              className="btn danger"
              onClick={() => void confirmUndo(confirming)}
            >
              Yes, remove {confirming.tradeIds.length} trade
              {confirming.tradeIds.length === 1 ? '' : 's'}
            </button>
            <button type="button" className="btn" onClick={() => setConfirming(null)}>
              Keep them
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
